/**
 * Sent Invitations Component for Sponsors
 * Displays sent invitations with status and withdraw functionality
 */

import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { getWeaveinApiUrl } from '@/src/config';
import {
  Send,
  Building2,
  Calendar,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Clock,
  Eye,
  RotateCcw,
  ChevronDown,
  ChevronUp,
  MessageSquare
} from 'lucide-react';
import { OutreachPanel } from './OutreachPanel';

import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';
interface SentInvitation {
  id: number;
  cbo_id: number;
  cbo_name: string;
  cbo_email?: string;
  cbo_phone?: string;
  cbo_fax?: string;
  program_id: number;
  program_name: string;
  status: string;
  invitation_date: string;
  response_date?: string;
  expires_at?: string;
  match_score?: number;
  invitation_message?: string;
}

interface SentInvitationsProps {
  sponsorId: number;
  programId?: number;
  onInvitationWithdrawn?: (invitationId: number) => void;
  refreshKey?: number;
}

export function SentInvitations({ sponsorId, programId, onInvitationWithdrawn, refreshKey }: SentInvitationsProps) {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const [invitations, setInvitations] = useState<SentInvitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedInvitation, setExpandedInvitation] = useState<number | null>(null);
  const [withdrawReason, setWithdrawReason] = useState('');
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [showWithdrawForm, setShowWithdrawForm] = useState<number | null>(null);

  useEffect(() => {
    loadInvitations();
  }, [sponsorId, programId, refreshKey]);

  const loadInvitations = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('auth_token');
      const cboId = localStorage.getItem('active_cbo_id');

      let url = getWeaveinApiUrl(`/api/sponsors/${sponsorId}/invitations`);
      if (programId) {
        url += `?program_id=${programId}`;
      }

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-CBO-ID': cboId || ''
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load invitations');
      }

      const data = await response.json();
      setInvitations(data);
    } catch (err) {
      console.error('Error loading invitations:', err);
      setError(err instanceof Error ? err.message : 'Failed to load invitations');
    } finally {
      setLoading(false);
    }
  };

  const handleWithdraw = async (invitationId: number) => {
    try {
      setActionLoading(invitationId);
      setError(null);

      const token = localStorage.getItem('auth_token');
      const cboId = localStorage.getItem('active_cbo_id');

      const response = await fetch(
        getWeaveinApiUrl(`/api/sponsor-invitations/${invitationId}/withdraw`),
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'X-CBO-ID': cboId || ''
          },
          body: JSON.stringify({
            withdrawal_reason: withdrawReason || null
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to withdraw invitation');
      }

      // Update invitation status in list
      setInvitations(invitations.map(inv =>
        inv.id === invitationId
          ? { ...inv, status: 'WITHDRAWN' }
          : inv
      ));
      setShowWithdrawForm(null);
      setWithdrawReason('');

      // Notify parent
      onInvitationWithdrawn?.(invitationId);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to withdraw invitation');
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (dateString: string) => {
    return fmt.medium(dateString);
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
      SENT: {
        label: 'Pending',
        color: 'bg-blue-100 text-blue-800',
        icon: <Clock className="w-3 h-3" />
      },
      VIEWED: {
        label: 'Viewed',
        color: 'bg-purple-100 text-purple-800',
        icon: <Eye className="w-3 h-3" />
      },
      ACCEPTED: {
        label: 'Accepted',
        color: 'bg-green-100 text-green-800',
        icon: <CheckCircle2 className="w-3 h-3" />
      },
      DECLINED: {
        label: 'Declined',
        color: 'bg-red-100 text-red-800',
        icon: <XCircle className="w-3 h-3" />
      },
      WITHDRAWN: {
        label: 'Withdrawn',
        color: 'bg-gray-100 text-gray-800',
        icon: <RotateCcw className="w-3 h-3" />
      },
      EXPIRED: {
        label: 'Expired',
        color: 'bg-amber-100 text-amber-800',
        icon: <AlertCircle className="w-3 h-3" />
      }
    };

    const config = statusConfig[status] || {
      label: status,
      color: 'bg-gray-100 text-gray-800',
      icon: null
    };

    return (
      <Badge className={`${config.color} flex items-center gap-1`}>
        {config.icon}
        {config.label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <Card className="p-6">
        <div className="flex items-center space-x-3">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
          <span className="text-gray-600">{t('sponsors.loading_invitations', 'Loading invitations...')}</span>
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="p-6 border-red-200 bg-red-50">
        <div className="flex items-start space-x-3">
          <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
          <div>
            <p className="font-medium text-red-900">{t('sponsors.error_loading_invitations', 'Error loading invitations')}</p>
            <p className="text-sm text-red-700">{error}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={loadInvitations}
              className="mt-2"
            >
              {t('sponsors.try_again', 'Try Again')}
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  if (invitations.length === 0) {
    return (
      <Card className="p-8 text-center">
        <Send className="w-12 h-12 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-600 font-medium">{t('sponsors.no_invitations_sent', 'No Invitations Sent')}</p>
        <p className="text-sm text-gray-500 mt-1">
          {t('sponsors.you_havent_sent_any_program', 'You haven\'t sent any program invitations yet.')}
        </p>
      </Card>
    );
  }

  // Group invitations by status
  const pendingInvitations = invitations.filter(inv => inv.status === 'SENT' || inv.status === 'VIEWED');
  const resolvedInvitations = invitations.filter(inv => !['SENT', 'VIEWED'].includes(inv.status));

  return (
    <div className="space-y-6">
      {/* Pending Invitations */}
      {pendingInvitations.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-semibold text-gray-900">{t('sponsors.pending_invitations', 'Pending Invitations')}</h4>
            <Badge className="bg-blue-100 text-blue-800">
              {pendingInvitations.length} pending
            </Badge>
          </div>

          <div className="space-y-3">
            {pendingInvitations.map((invitation) => (
              <Card key={invitation.id} className="p-4">
                {/* Header */}
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-gray-400" />
                      <span className="font-medium text-gray-900">{invitation.cbo_name}</span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">{invitation.program_name}</p>
                    <div className="flex items-center gap-3 mt-2">
                      {getStatusBadge(invitation.status)}
                      {invitation.match_score && (
                        <span className="text-xs text-gray-500">
                          Match: {invitation.match_score.toFixed(0)}%
                        </span>
                      )}
                    </div>
                  </div>

                  <button
                    onClick={() => setExpandedInvitation(
                      expandedInvitation === invitation.id ? null : invitation.id
                    )}
                    className="p-1 hover:bg-gray-100 rounded"
                  >
                    {expandedInvitation === invitation.id ? (
                      <ChevronUp className="w-4 h-4 text-gray-500" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-gray-500" />
                    )}
                  </button>
                </div>

                {/* Meta */}
                <div className="flex items-center gap-4 mt-3 text-xs text-gray-500">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    Sent: {formatDate(invitation.invitation_date)}
                  </span>
                  {invitation.expires_at && (
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      Expires: {formatDate(invitation.expires_at)}
                    </span>
                  )}
                </div>

                {/* Expanded Content */}
                {expandedInvitation === invitation.id && (
                  <div className="mt-4 pt-4 border-t space-y-4">
                    {/* Withdraw Form */}
                    {showWithdrawForm === invitation.id ? (
                      <div className="bg-amber-50 p-4 rounded-lg border border-amber-100">
                        <h5 className="font-medium text-amber-900 mb-3">{t('sponsors.withdraw_invitation', 'Withdraw Invitation')}</h5>
                        <div className="mb-3">
                          <Label className="text-sm text-amber-800">
                            Reason for withdrawing (optional)
                          </Label>
                          <Textarea
                            value={withdrawReason}
                            onChange={(e) => setWithdrawReason(e.target.value)}
                            placeholder={t('sponsors.internal_note_about_why_youre', 'Internal note about why you\'re withdrawing...')}
                            rows={3}
                            className="mt-1"
                          />
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setShowWithdrawForm(null);
                              setWithdrawReason('');
                            }}
                            disabled={actionLoading === invitation.id}
                          >
                            {t('sponsors.cancel', 'Cancel')}
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handleWithdraw(invitation.id)}
                            disabled={actionLoading === invitation.id}
                            className="bg-amber-600 hover:bg-amber-700"
                          >
                            {actionLoading === invitation.id ? 'Withdrawing...' : 'Confirm Withdraw'}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {/* Invitation Message */}
                        {invitation.invitation_message && (
                          <div className="bg-gray-50 p-3 rounded-lg">
                            <div className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                              <MessageSquare className="w-4 h-4" />
                              {t('sponsors.invitation_message', 'Invitation Message')}
                            </div>
                            <p className="text-sm text-gray-600 whitespace-pre-wrap">
                              {invitation.invitation_message}
                            </p>
                          </div>
                        )}

                        {/* Outreach Panel */}
                        <OutreachPanel
                          invitationId={invitation.id}
                          cboName={invitation.cbo_name}
                          cboEmail={invitation.cbo_email}
                          cboPhone={invitation.cbo_phone}
                          cboFax={invitation.cbo_fax}
                          onOutreachSent={loadInvitations}
                        />

                        {/* Actions */}
                        <div className="flex gap-3">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setShowWithdrawForm(invitation.id)}
                            className="text-amber-600 hover:bg-amber-50 hover:border-amber-300"
                          >
                            <RotateCcw className="w-4 h-4 mr-2" />
                            {t('sponsors.withdraw', 'Withdraw')}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Resolved Invitations */}
      {resolvedInvitations.length > 0 && (
        <div>
          <h4 className="font-semibold text-gray-900 mb-3">{t('sponsors.invitation_history', 'Invitation History')}</h4>
          <div className="space-y-2">
            {resolvedInvitations.map((invitation) => (
              <Card key={invitation.id} className="p-3 bg-gray-50">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm text-gray-900">{invitation.cbo_name}</span>
                      {getStatusBadge(invitation.status)}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {invitation.program_name}
                      {invitation.response_date && ` • ${formatDate(invitation.response_date)}`}
                    </p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default SentInvitations;
